import { Head, Link, router } from '@inertiajs/react'
import AdminLayout from '../../../components/layout/AdminLayout'

export default function AdminAgenciesShow({ agency }) {
  const handleDelete = () => {
    if (confirm('Supprimer cette agence ? Cette action est irréversible.')) {
      router.delete(`/admin/agencies/${agency.id}`)
    }
  }

  const handleToggleActive = () => {
    router.patch(`/admin/agencies/${agency.id}`, {
      agency: { active: !agency.active }
    })
  }

  return (
    <AdminLayout>
      <Head title={`${agency.name} - Administration`} />

      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              href="/admin/agencies"
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{agency.name}</h1>
              <p className="mt-1 text-sm text-gray-600">{agency.city}</p>
            </div>
            <StatusBadge active={agency.active} />
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleToggleActive}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                agency.active
                  ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                  : 'bg-green-100 text-green-800 hover:bg-green-200'
              }`}
            >
              {agency.active ? 'Désactiver' : 'Activer'}
            </button>
            <Link
              href={`/admin/agencies/${agency.id}/edit`}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
            >
              Modifier
            </Link>
            <button
              onClick={handleDelete}
              className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition-colors"
            >
              Supprimer
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-3">
          <StatsCard title="Leads" value={agency.stats.leadsCount} color="blue" />
          <StatsCard title="QR Codes" value={agency.stats.qrCodesCount} color="indigo" />
          <StatsCard title="Utilisateurs" value={agency.stats.usersCount} color="gray" />
        </div>

        {/* Info + Users */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Agency Info */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Informations</h2>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InfoField label="Email" value={agency.email} />
              <InfoField label="Téléphone" value={agency.phone} />
              <InfoField label="Adresse" value={agency.address} />
              <InfoField label="Ville" value={agency.city} />
              <InfoField label="Code postal" value={agency.postalCode} />
              <InfoField label="Ton IA" value={agency.aiTone} />
            </dl>
            {agency.aiPrompt && (
              <div className="mt-4">
                <dt className="text-sm font-medium text-gray-500">Prompt IA</dt>
                <dd className="mt-1 text-sm text-gray-900 bg-gray-50 rounded p-3">{agency.aiPrompt}</dd>
              </div>
            )}
            <div className="mt-4 text-xs text-gray-400">
              Créée le {new Date(agency.createdAt).toLocaleDateString('fr-FR')} — Mise à jour le {new Date(agency.updatedAt).toLocaleDateString('fr-FR')}
            </div>
          </div>

          {/* Users */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Utilisateurs ({agency.users.length})</h2>
            {agency.users.length === 0 ? (
              <p className="text-sm text-gray-500">Aucun utilisateur</p>
            ) : (
              <div className="space-y-3">
                {agency.users.map((user) => (
                  <div key={user.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-200">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{user.email}</div>
                      <div className="text-xs text-gray-500">{user.role}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {user.verified ? (
                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded bg-green-100 text-green-800">Vérifié</span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded bg-yellow-100 text-yellow-800">Non vérifié</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* QR Codes */}
        {agency.qrCodes.length > 0 && (
          <div className="mb-6 bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">QR Codes</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campagne</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tracking ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scans</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {agency.qrCodes.map((qr) => (
                    <tr key={qr.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{qr.campaignName || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 font-mono">{qr.trackingId}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{qr.scansCount}</td>
                      <td className="px-4 py-3">
                        <StatusBadge active={qr.active} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Recent Leads */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Leads récents</h2>
          {agency.recentLeads.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">Aucun lead pour le moment</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nom</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {agency.recentLeads.map((lead) => (
                    <tr key={lead.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {lead.firstName} {lead.lastName}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{lead.email}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{translateProjectType(lead.projectType)}</td>
                      <td className="px-4 py-3">
                        <LeadStatusBadge status={lead.status} />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {new Date(lead.createdAt).toLocaleDateString('fr-FR')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  )
}

function InfoField({ label, value }) {
  return (
    <div>
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900">{value || '-'}</dd>
    </div>
  )
}

function StatsCard({ title, value, color = 'blue' }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-700',
    green: 'bg-green-50 text-green-700',
    indigo: 'bg-indigo-50 text-indigo-700',
    gray: 'bg-gray-50 text-gray-700',
  }

  return (
    <div className="overflow-hidden bg-white rounded-lg shadow">
      <div className="p-6">
        <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
        <dd className={`mt-2 text-3xl font-semibold ${colorClasses[color]}`}>{value}</dd>
      </div>
    </div>
  )
}

function StatusBadge({ active }) {
  if (active) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        Active
      </span>
    )
  }
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
      Inactive
    </span>
  )
}

function LeadStatusBadge({ status }) {
  const config = {
    new: { label: 'Nouveau', color: 'bg-gray-100 text-gray-800' },
    contacted: { label: 'Contacté', color: 'bg-yellow-100 text-yellow-800' },
    qualified: { label: 'Qualifié', color: 'bg-blue-100 text-blue-800' },
    converted: { label: 'Converti', color: 'bg-green-100 text-green-800' },
    lost: { label: 'Perdu', color: 'bg-red-100 text-red-800' },
  }

  const { label, color } = config[status] || config.new

  return (
    <span className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full ${color}`}>
      {label}
    </span>
  )
}

function translateProjectType(type) {
  const translations = {
    buyer: 'Acheteur',
    seller: 'Vendeur',
    tenant: 'Locataire',
    landlord: 'Propriétaire',
  }
  return translations[type] || type
}
